<div class="master-controller-form-container p-6 max-w-4xl mx-auto">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-4">
            <button mat-icon-button (click)="navigateBack()" class="back-button">
                <mat-icon>arrow_back</mat-icon>
            </button>
            <div>
                <h1 class="text-3xl font-bold text-gray-900">{{ isEditMode() ? 'Edit' : 'Add' }} Master Controller</h1>
                <p class="text-sm text-gray-600 mt-1">Manage Raspberry Pi, Gateway, or IoT device configuration</p>
            </div>
        </div>
    </div>

    @if (isLoading()) {
    <div class="flex items-center justify-center py-12">
        <mat-progress-spinner mode="indeterminate" diameter="50"></mat-progress-spinner>
    </div>
    } @else {
    <form [formGroup]="masterControllerForm" (ngSubmit)="onSubmit()" class="space-y-6">
        <!-- Basic Information Card -->
        <jjames-card>
            <div class="p-6">
                <h2 class="text-xl font-semibold mb-4">Basic Information</h2>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Device Name</mat-label>
                        <input matInput formControlName="deviceName" required>
                        @if (masterControllerForm.get('deviceName')?.hasError('required')) {
                        <mat-error>
                            Device name is required
                        </mat-error>
                        }
                    </mat-form-field>

                    <div class="flex gap-2 items-end">
                        <mat-form-field appearance="fill" class="flex-1">
                            <mat-label>Hardware ID</mat-label>
                            <input matInput formControlName="deviceHardwareID" required readonly>
                            @if (masterControllerForm.get('deviceHardwareID')?.hasError('required')) {
                            <mat-error>
                                Hardware ID is required
                            </mat-error>
                            }
                        </mat-form-field>
                        <button mat-mini-fab color="accent" type="button" (click)="generateHardwareID()"
                            matTooltip="Generate new Hardware ID">
                            <mat-icon>refresh</mat-icon>
                        </button>
                    </div>

                    <mat-form-field appearance="fill" class="w-full md:col-span-2">
                        <mat-label>Description</mat-label>
                        <textarea matInput formControlName="deviceDescription" rows="3"></textarea>
                    </mat-form-field>
                </div>
            </div>
        </jjames-card>

        <!-- IoT Hub Configuration -->
        <jjames-card>
            <div class="p-6">
                <h2 class="text-xl font-semibold mb-4">Azure IoT Hub Configuration</h2>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>IoT Hub Device ID</mat-label>
                        <input matInput formControlName="iotHubDeviceId" required>
                        @if (masterControllerForm.get('iotHubDeviceId')?.hasError('required')) {
                        <mat-error>
                            IoT Hub Device ID is required
                        </mat-error>
                        }
                        @if (masterControllerForm.get('iotHubDeviceId')?.hasError('notUnique')) {
                        <mat-error>
                            This IoT Hub Device ID is already in use
                        </mat-error>
                        }
                        <mat-hint>Must be unique across all master controllers</mat-hint>
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>IoT Hub Host Name</mat-label>
                        <input matInput formControlName="iotHubHostName">
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Authentication Method</mat-label>
                        <input matInput formControlName="authenticationMethod"
                            placeholder="e.g., symmetric_key, certificate">
                    </mat-form-field>
                </div>
            </div>
        </jjames-card>

        <!-- Hardware Information -->
        <jjames-card>
            <div class="p-6">
                <h2 class="text-xl font-semibold mb-4">Hardware Information</h2>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Operating System</mat-label>
                        <input matInput formControlName="operatingSystem" placeholder="e.g., Linux, Windows">
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Firmware Version</mat-label>
                        <input matInput formControlName="firmwareVersion">
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>CPU Model</mat-label>
                        <input matInput formControlName="cpuModel" placeholder="e.g., ARM Cortex-A72">
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>RAM (MB)</mat-label>
                        <input matInput formControlName="ramMb" type="number">
                        @if (masterControllerForm.get('ramMb')?.hasError('pattern')) {
                        <mat-error>
                            Must be a number
                        </mat-error>
                        }
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Storage (GB)</mat-label>
                        <input matInput formControlName="storageGb" type="number">
                        @if (masterControllerForm.get('storageGb')?.hasError('pattern')) {
                        <mat-error>
                            Must be a number
                        </mat-error>
                        }
                    </mat-form-field>
                </div>
            </div>
        </jjames-card>

        <!-- Network Information -->
        <jjames-card>
            <div class="p-6">
                <h2 class="text-xl font-semibold mb-4">Network Information</h2>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>MAC Address</mat-label>
                        <input matInput formControlName="macAddress" placeholder="e.g., 00:1A:2B:3C:4D:5E">
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>IP Address</mat-label>
                        <input matInput formControlName="ipAddress" placeholder="e.g., 192.168.1.100">
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Hostname</mat-label>
                        <input matInput formControlName="hostname">
                    </mat-form-field>
                </div>
            </div>
        </jjames-card>

        <!-- Address Information -->
        <jjames-card>
            <div class="p-6">
                <h2 class="text-xl font-semibold mb-4">Physical Location</h2>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <mat-form-field appearance="fill" class="w-full md:col-span-2">
                        <mat-label>Address Line 1</mat-label>
                        <input matInput formControlName="addressLine1">
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full md:col-span-2">
                        <mat-label>Address Line 2</mat-label>
                        <input matInput formControlName="addressLine2">
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>City</mat-label>
                        <input matInput formControlName="city">
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Postal Code</mat-label>
                        <input matInput formControlName="postalCode">
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Region</mat-label>
                        <input matInput formControlName="region">
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Country Code</mat-label>
                        <input matInput formControlName="countryCode" placeholder="e.g., US, UK">
                    </mat-form-field>
                </div>
            </div>
        </jjames-card>

        <!-- Assignment Configuration -->
        @if (!isEditMode()) {
        <jjames-card>
            <div class="p-6">
                <h2 class="text-xl font-semibold mb-4">Assignment Configuration</h2>
                <div class="space-y-4">
                    <!-- Assignment Type Selection -->
                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Assignment Type</mat-label>
                        <mat-select formControlName="assignmentType">
                            <mat-option value="InfrastructureWide">
                                <mat-icon class="mr-2">grid_on</mat-icon> Infrastructure Wide
                            </mat-option>
                            <mat-option value="GroupSpecific">
                                <mat-icon class="mr-2">category</mat-icon> Group Specific
                            </mat-option>
                        </mat-select>
                        <mat-hint>Choose how this master controller manages devices</mat-hint>
                    </mat-form-field>

                    <!-- Infrastructure Wide - Show Infrastructures -->
                    @if (masterControllerForm.get('assignmentType')?.value === 'InfrastructureWide') {
                    <div class="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h3 class="font-semibold text-sm mb-3 flex items-center gap-2">
                            <mat-icon class="text-blue-600">grid_on</mat-icon>
                            Controlled Infrastructures
                        </h3>
                        <p class="text-xs text-gray-600 mb-3">This master controller will manage all devices in the
                            selected infrastructures</p>

                        @if (infrastructures().length === 0) {
                        <p class="text-sm text-gray-500 py-2">No infrastructures available</p>
                        } @else {
                        <div class="space-y-2">
                            @for (infra of infrastructures(); track infra.id) {
                            <label class="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer">
                                <input type="checkbox" [checked]="isInfrastructureSelected(infra.id!)"
                                    (change)="toggleInfrastructure(infra.id!)" class="cursor-pointer">
                                <span class="text-sm">
                                    <span class="font-medium">{{ infrastructureLabel(infra) }}</span>
                                    <span class="text-gray-500"> · {{ infra.infrastructureType }} · {{ infra.deviceGroups?.length || 0 }} groups</span>
                                </span>
                            </label>
                            }
                        </div>
                        }
                    </div>
                    }

                    <!-- Group Specific - Show Device Groups -->
                    @if (masterControllerForm.get('assignmentType')?.value === 'GroupSpecific') {
                    <div class="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                        <h3 class="font-semibold text-sm mb-3 flex items-center gap-2">
                            <mat-icon class="text-green-600">category</mat-icon>
                            Controlled Device Groups
                        </h3>
                        <p class="text-xs text-gray-600 mb-3">This master controller will manage only the selected
                            device groups</p>

                        @if (deviceGroups().length === 0) {
                        <p class="text-sm text-gray-500 py-2">No device groups available</p>
                        } @else {
                        <div class="space-y-2">
                            @for (group of deviceGroups(); track group.id) {
                            <label class="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer">
                                <input type="checkbox" [checked]="isDeviceGroupSelected(group.id!)"
                                    (change)="toggleDeviceGroup(group.id!)" class="cursor-pointer">
                                <span class="text-sm">
                                    <span class="font-medium">{{ group.groupName }}</span>
                                    <span class="text-gray-500">
                                        @if (deviceGroupOwner(group); as owner) { · {{ owner }} }
                                        · {{ group.groupType }}
                                    </span>
                                </span>
                            </label>
                            }
                        </div>
                        }
                    </div>
                    }
                </div>
            </div>
        </jjames-card>
        }

        <!-- Form Actions -->
        <div class="flex items-center justify-end gap-3 pt-4">
            <jjames-button [variant]="'secondary'" (clicked)="navigateBack()">
                Cancel
            </jjames-button>
            <jjames-button [variant]="'primary'" (clicked)="onSubmit()"
                [disabled]="masterControllerForm.invalid || isSaving()" [matIconLeft]="isSaving() ? '' : 'save'">
                {{ isSaving() ? 'Saving...' : 'Save Master Controller' }}
            </jjames-button>
        </div>
    </form>
    }
</div>